import { ApolloClient, ApolloProvider, InMemoryCache, from, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { useLanguageCode } from '@procureai/ui-mui';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { PropsWithChildren, useEffect, useMemo } from 'react';

import { appConfig } from '~/common/config';
import { registerActiveScopeChangeHandler } from '~/services/activeScope';
import { fetchClient } from '~/services/http';
import { offsetLimitNodesPagination } from '~/util/apollo';
import { SseLink } from '~/util/apollo/sseLink';

export const ApolloAuthProvider = ({ children }: PropsWithChildren) => {
  const { languageCode } = useLanguageCode();

  const client = useMemo(() => {
    // Singular (the datagraph) authenticates via `Authorization: Bearer` and uses no cookies, so
    // its requests must NOT be credentialed — otherwise the browser requires Access-Control-Allow-
    // Credentials, which the gateway (correctly) doesn't send, and drops the response. Use a
    // credentials-omitting variant for the datagraph; the shared fetchClient keeps credentials for
    // the cookie-bearing endpoints (core/mastra) it is also used for.
    const datagraphFetch = fetchClient.extend({ credentials: 'omit' });
    const sseLink = new SseLink({
      url: appConfig.datagraph.url,
      fetchFn: datagraphFetch,
    });

    const uploadLink = createUploadLink({
      uri: appConfig.datagraph.url,
      fetch: datagraphFetch,
    });

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      sseLink,
      uploadLink,
    );

    return new ApolloClient({
      link: from([splitLink]),
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              sevenbridges: {
                merge: (existing, incoming) => ({
                  ...existing,
                  ...incoming,
                }),
              },
              core: {
                merge: (existing, incoming) => ({
                  ...existing,
                  ...incoming,
                }),
              },
              tesseract: {
                merge: (existing, incoming) => ({
                  ...existing,
                  ...incoming,
                }),
              },
              astronaut: {
                merge: (existing, incoming) => ({
                  ...existing,
                  ...incoming,
                }),
              },
              contracts: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              suppliers: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              commodities: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              orders: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              purchaseRequisitions: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              claraCases: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
              claraDemands: offsetLimitNodesPagination(['order', 'orderBy', 'filter', 'filterBy']),
            },
          },
          Subscription: {
            fields: {
              core: {
                merge: (existing, incoming) => ({
                  ...existing,
                  ...incoming,
                }),
              },
            },
          },
          coreQuery: {
            fields: {
              accountingAssets: offsetLimitNodesPagination(['order', 'filter']),
              accountingTypes: offsetLimitNodesPagination(['order', 'filter']),
              commodities: offsetLimitNodesPagination(['order', 'filter']),
              commodityGroups: offsetLimitNodesPagination(['order', 'filter']),
              companies: offsetLimitNodesPagination(['order', 'filter']),
              documents: offsetLimitNodesPagination(['order', 'filter']),
              emails: offsetLimitNodesPagination(['order', 'filter']),
              glAccounts: offsetLimitNodesPagination(['order', 'filter']),
              inboundPipelines: offsetLimitNodesPagination(['order', 'filter']),
              internalOrders: offsetLimitNodesPagination(['order', 'filter']),
              locations: offsetLimitNodesPagination(['order', 'filter']),
              orderConfirmations: offsetLimitNodesPagination(['order', 'filter']),
              organizationalUnits: offsetLimitNodesPagination(['order', 'filter']),
              outboundPipelines: offsetLimitNodesPagination(['order', 'filter']),
              purchaseOrders: offsetLimitNodesPagination(['order', 'filter']),
              units: offsetLimitNodesPagination(['order', 'filter']),
              workPackages: offsetLimitNodesPagination(['order', 'filter']),
            },
          },
          tesseractQuery: {
            fields: {
              negotiationCases: offsetLimitNodesPagination(['order', 'filter']),
              demands: offsetLimitNodesPagination(['order', 'filter']),
            },
          },
          TsrctRuleListQueries: {
            fields: {
              sourcing: offsetLimitNodesPagination(['order', 'filter']),
            },
          },
        },
        possibleTypes: {
          TsrctQuoteInterface: ['TsrctQuote', 'TsrctNegotiatedQuote'],
          TsrctNegotiatedQuoteInterface: ['TsrctNegotiatedQuote'],
        },
      }),
    });
    // we want the client to be re-created when the language code changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageCode]);

  useEffect(() => {
    // listens to scope changes
    return registerActiveScopeChangeHandler(async () => {
      // clears/refetches active queries
      await client.resetStore();
    });
  }, [client]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
